// Settings → Integrations: the calendar OAuth client credentials.
//
// This exists so turning on calendar sync is an admin job in the app rather
// than an operator job on the VPS. One step stays outside and always will —
// creating the OAuth client in Google Cloud Console / Microsoft Entra, because
// that is where the credentials are issued. Everything this screen can do is
// aimed at making the paste-back easy and hard to get wrong, which is why the
// redirect URI is shown with a copy button and why Test exists at all: a
// redirect_uri mismatch and a mistyped secret are the two failures that
// otherwise surface much later as an opaque error mid-consent.
//
// The client ID/secret identify the APPLICATION, not a person. Each user still
// links their own Google account from the Calendar page and gets their own
// tokens; nothing here is shared between users' calendars.

import { useState } from 'react';
import { can } from '../../auth/store';
import { apiErrorText } from '../../utils/apiErrors';
import { fmtDateTime } from '../../utils/date';
import {
  useOAuthIntegrations,
  useSaveOAuthIntegration,
  useRemoveOAuthIntegration,
  useTestOAuthIntegration,
  type OAuthIntegration,
  type ProbeResult,
} from '../../api/queries/integrations';
import './IntegrationsTab.css';

const PROBE_TONE: Record<ProbeResult, string> = {
  accepted: 'int-probe-ok',
  rejected: 'int-probe-bad',
  unknown: 'int-probe-unsure',
};

function StatusPill({ integration }: { integration: OAuthIntegration }): JSX.Element {
  if (integration.source === 'settings') {
    return <span className="int-pill int-pill-on">Configured</span>;
  }
  if (integration.source === 'env') {
    // Worth distinguishing: edits here would move the source of truth, and an
    // admin should know the server env is currently winning.
    return <span className="int-pill int-pill-env">Configured via environment</span>;
  }
  return <span className="int-pill int-pill-off">Not configured</span>;
}

function ProviderCard({ integration }: { integration: OAuthIntegration }): JSX.Element {
  const save = useSaveOAuthIntegration();
  const remove = useRemoveOAuthIntegration();
  const test = useTestOAuthIntegration();
  const canWrite = can('settings.write_security');

  const [clientId, setClientId] = useState(integration.client_id ?? '');
  const [clientSecret, setClientSecret] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [probe, setProbe] = useState<{ result: ProbeResult; message: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const onSave = async () => {
    setError(null);
    setSaved(false);
    setProbe(null);
    try {
      await save.mutateAsync({
        provider: integration.provider,
        client_id: clientId.trim(),
        // Blank means "leave the stored one alone" — the admin can't read it
        // back, so making them re-enter it to fix a typo in the ID would send
        // them to the provider console for no reason.
        ...(clientSecret.trim() !== '' ? { client_secret: clientSecret.trim() } : {}),
      });
      setClientSecret('');
      setSaved(true);
    } catch (err) {
      setError(apiErrorText(err, 'Could not save these credentials.'));
    }
  };

  const onTest = async () => {
    setError(null);
    setProbe(null);
    try {
      const res = await test.mutateAsync(integration.provider);
      setProbe({ result: res.result, message: res.message });
    } catch (err) {
      setError(apiErrorText(err, 'Could not test these credentials.'));
    }
  };

  const onRemove = async () => {
    setError(null);
    setProbe(null);
    if (!confirm(
      `Remove the ${integration.label} credentials? Nobody will be able to connect a new
       calendar until they are replaced. Already-linked accounts stop syncing once their
       current token expires.`.replace(/\s+/g, ' '),
    )) return;
    try {
      await remove.mutateAsync(integration.provider);
      setClientId('');
      setClientSecret('');
      setSaved(false);
    } catch (err) {
      setError(apiErrorText(err, 'Could not remove these credentials.'));
    }
  };

  const onCopyRedirect = async () => {
    try {
      await navigator.clipboard.writeText(integration.redirect_uri);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard is permission-gated and blocked outright in some contexts.
      // The URI is selectable on screen, so this is a convenience, not the
      // only way to get the value.
      setError('Could not copy — select the URI and copy it manually.');
    }
  };

  const busy = save.isPending || remove.isPending || test.isPending;

  return (
    <div className="card int-card">
      <header className="int-card-head">
        <h3>{integration.label}</h3>
        <StatusPill integration={integration} />
      </header>

      <label className="int-field">
        <span>Client ID</span>
        <input
          type="text"
          className="form-input"
          value={clientId}
          onChange={(e) => setClientId(e.target.value)}
          placeholder={integration.provider === 'google'
            ? '000000000000-xxxxxxxx.apps.googleusercontent.com'
            : 'Application (client) ID'}
          disabled={!canWrite || busy}
          spellCheck={false}
        />
      </label>

      <label className="int-field">
        <span>
          Client secret
          <span className="int-secret-state">
            {integration.has_secret ? 'stored' : 'not set'}
          </span>
        </span>
        <input
          type="password"
          className="form-input"
          value={clientSecret}
          onChange={(e) => setClientSecret(e.target.value)}
          placeholder={integration.has_secret
            ? 'Leave blank to keep the stored secret'
            : 'Paste the secret from the provider console'}
          disabled={!canWrite || busy}
          spellCheck={false}
          autoComplete="new-password"
        />
      </label>

      <div className="int-field">
        <span className="int-label">Redirect URI</span>
        <p className="int-help">
          Add this to the provider console exactly as written — it is compared
          character for character, and a mismatch is rejected before anyone sees
          a consent screen.
        </p>
        <div className="int-redirect">
          <code>{integration.redirect_uri}</code>
          <button type="button" className="int-copy" onClick={() => { void onCopyRedirect(); }}>
            {copied ? 'copied' : 'copy'}
          </button>
        </div>
      </div>

      {integration.updated_at !== null && (
        <p className="int-meta">
          Secret last set {fmtDateTime(integration.updated_at)}
          {integration.updated_by_name !== null && ` by ${integration.updated_by_name}`}.
          Its value is never shown again.
        </p>
      )}

      {error !== null && <p className="int-banner int-banner-bad" role="alert">{error}</p>}
      {saved && probe === null && (
        <p className="int-banner int-banner-ok" role="status">
          Saved. Use Test to check the credentials before connecting.
        </p>
      )}
      {probe !== null && (
        <p className={`int-banner ${PROBE_TONE[probe.result]}`} role="status">{probe.message}</p>
      )}

      {canWrite && (
        <div className="int-actions">
          {(integration.source === 'settings') && (
            <button
              type="button"
              className="int-remove"
              onClick={() => { void onRemove(); }}
              disabled={busy}
            >
              Remove
            </button>
          )}
          <button
            type="button"
            className="btn-charcoal-secondary"
            onClick={() => { void onTest(); }}
            disabled={busy || integration.source === null}
          >
            {test.isPending ? 'Testing…' : 'Test'}
          </button>
          <button
            type="button"
            className="btn-cyan-primary"
            onClick={() => { void onSave(); }}
            disabled={busy || clientId.trim() === ''}
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      )}
    </div>
  );
}

export function IntegrationsTab(): JSX.Element {
  const { data, isPending, error } = useOAuthIntegrations();

  if (isPending) return <p className="int-status">Loading integrations…</p>;
  if (error) return <p className="int-status int-status-error">{apiErrorText(error, 'Failed to load.')}</p>;

  const providers = data?.providers ?? [];

  return (
    <div className="int-wrap">
      <p className="int-intro">
        Credentials for the calendar sync on the Calendar page. They identify
        this application to the provider — each person still connects their own
        account and keeps their own events. Create the OAuth client in Google
        Cloud Console or Microsoft Entra, then paste its ID and secret here.
      </p>
      {providers.map((p) => <ProviderCard key={p.provider} integration={p} />)}
    </div>
  );
}
